import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { AdminListInterface as OwnerUser, RoleListInterface } from '../../../modules/accounts/components/settings/SettingsModel'; // Renamed for clarity
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  delete_owner_user,
  get_all_owner_users, get_all_owner_users_paginated,
  update_owner_user
} from '../api/usersApi';
import { toast } from 'react-toastify';
import { KTIcon, KTSVG } from '../../../../_metronic/helpers';
import { Form } from 'react-bootstrap';
import { get_active_roles } from '../../roles/api/rolesApi';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';
import { validateImage } from '../../../helper-functions/ImageValidation';
import { formatDateWithTime } from '../../../helper-functions/DateFunction';

// --- Type Definitions ---
// Using AdminListInterface as OwnerUser for now, you can create a more specific OwnerUser type
// based on the actual fields returned by get_all_owner_users API.

type ApiPaginationInfo = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
};

type ApiOwnerUsersDataPayload = {
    users: OwnerUser[]; // Assuming API returns 'users' array
    pagination: ApiPaginationInfo;
    links: { /* ... link structure ... */ };
};

type ApiOwnerUsersFullResponse = {
    data: { // From Axios
        success: boolean;
        message: string;
        data: ApiOwnerUsersDataPayload; // From your API structure
    };
};
// --- End Type Definitions ---


var user_create:boolean, user_update:boolean, user_delete:boolean, user_view:boolean, user_activity_log:boolean;

function UsersList() { // Renamed component if it's specific to Owner Users
    const [usersList, setUsersList] = useState<OwnerUser[]>([]); // For current page's users

    // Filter States (Add status filter if applicable for owner users)
    // const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [searchInput, setSearchInput] = useState<string>('');

    // State for API filter payload
    const [filterPayloadForApi, setFilterPayloadForApi] = useState<any>({
        // status: '', // If you add status filter
        // searchTerm: '', // If backend supports search
    });

    // Pagination State
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10); // Renamed
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [currentRangeFrom, setCurrentRangeFrom] = useState<number>(1);
    const [currentRangeTo, setCurrentRangeTo] = useState<number>(0);
    
    // const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null); // Server-side sorting

    const [selectedAdmin, setSelectedAdmin] = useState<OwnerUser | null>(null); // Renamed from selectedAdmin
    const [userPicture, setUserPicture] = useState<File | null>(null);
    const [lastNameError, setLastNameError] = useState('');
    const [firstNameValidationError, setFirstNameValidationError] = useState('');
    // const [emailmailValidationError, setEmailValidationError] = useState(''); // Not used in your provided code
    
    const imageRef = useRef<HTMLInputElement>(null);
    // const [roleList, setRoleList] = useState<RoleListInterface[]>([]); // Not used for Owner Users list display

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    user_create = Boolean(permissionCheck('user-create')); // Or specific owner-user permissions
    user_update = Boolean(permissionCheck('user-update'));
    user_delete = Boolean(permissionCheck('user-delete'));
    user_view = Boolean(permissionCheck('user-view'));
    user_activity_log = Boolean(permissionCheck('user-activity-log'));

    // Fetch active roles if needed for the edit modal, otherwise can be removed if not used.
    // const { data:dataRoles } = useQuery('get_active_roles_for_owner_edit', get_active_roles, { refetchOnWindowFocus: false });
    // useEffect(() => {
    //   if (dataRoles?.data) {
    //     setRoleList(dataRoles.data)
    //   }
    // }, [dataRoles]);


    // --- Main Query for Paginated Owner Users ---
    const {
        isLoading,
        isFetching,
        isError,
        error,
    } = useQuery<ApiOwnerUsersFullResponse, Error>(
        // Query key now includes all parameters that trigger a refetch
        ['all_owner_users', filterPayloadForApi, currentPage, itemsPerPage],
        () => get_all_owner_users_paginated(
            currentPage,
            itemsPerPage,
            // filterPayloadForApi.status, // If status filter is added
            // filterPayloadForApi.searchTerm, // If server-side search
        ),
        {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            onSuccess: (response) => {
                const apiPayload = response.data.data;
                // Assuming API returns 'users' array
                if (apiPayload && apiPayload.users && Array.isArray(apiPayload.users)) {
                    setUsersList(apiPayload.users); // Set current page's users
                    if (apiPayload.pagination) {
                        setTotalItems(apiPayload.pagination.total);
                        setTotalPages(apiPayload.pagination.last_page);
                        // setCurrentPage(apiPayload.pagination.current_page); // Sync only if necessary
                        setCurrentRangeFrom(apiPayload.pagination.from);
                        setCurrentRangeTo(apiPayload.pagination.to);
                    }
                } else {
                    setUsersList([]);
                    setTotalItems(0);
                    setTotalPages(0);
                    setCurrentRangeFrom(1);
                    setCurrentRangeTo(0);
                }
            },
            onError: (error: any) => {
                toast.error(error.message || "Failed to fetch owner users.", { autoClose: 3000 });
                setUsersList([]);
                setTotalItems(0);
                setTotalPages(0);
            },
        }
    );
  
    // Client-side search (optional, can be moved to server-side)
    const displayedUsersList = searchInput
        ? usersList.filter((user) =>
            user.first_name?.toLowerCase().includes(searchInput.toLowerCase()) ||
            user.full_name?.toLowerCase().includes(searchInput.toLowerCase()) ||
            user.last_name?.toLowerCase().includes(searchInput.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchInput.toLowerCase())
          )
        : usersList;

    // Mutations
    const handleDeleteAdmin = useMutation( // Renamed to handleDeleteOwnerUser
        (id: number) => delete_owner_user(id),
        {
            onSuccess: (response) => {
                toast.error(response.message || "Owner user deleted.", { autoClose: 3000 }); // Error toast for delete?
                queryClient.invalidateQueries(['all_owner_users', filterPayloadForApi, currentPage, itemsPerPage]);
            },
            onError: (error: any) => {
                const errorMessage = error.response?.data?.message || error.message || "Failed to delete owner user.";
                toast.error(errorMessage, { autoClose: 3000 });
            },
        }
    );

    const handleUpdateAdmin = useMutation( // Renamed to handleUpdateOwnerUser
        (updatedAdminData: { id: number; first_name: string; last_name?: string; middle_name?: string; mobile_no?: string | number; status: number; userPicture?: File | null }) => {
            const formData = new FormData();
            formData.append('first_name', updatedAdminData.first_name);
            formData.append('last_name', updatedAdminData.last_name || '');
            formData.append('middle_name', updatedAdminData.middle_name || '');
            formData.append('mobile_no', updatedAdminData.mobile_no?.toString() || '');
            formData.append('status', updatedAdminData.status.toString());
            if (updatedAdminData.userPicture) {
                formData.append('profile_picture', updatedAdminData.userPicture);
            }
            // If your API expects _method: 'PUT' for updates with FormData
            // formData.append('_method', 'PUT'); 

            return update_owner_user(updatedAdminData.id, formData);
        },
        {
            onSuccess: (response) => { // response here is data from update_owner_user (response.data from axios)
                toast.success(response.message || "Owner user updated successfully.", { autoClose: 3000 });
                queryClient.invalidateQueries(['all_owner_users', filterPayloadForApi, currentPage, itemsPerPage]);
                setUserPicture(null); // Reset picture state
                if(imageRef.current) imageRef.current.value = ''; // Clear file input
            },
            onError: (error: any) => {
                const errorMessage = error.response?.data?.message || error.message || "Failed to update owner user.";
                toast.error(<div dangerouslySetInnerHTML={{ __html: errorMessage }} />, { autoClose: 6000 });
            },
        }
    );


    // Filter change handler (Example if you add status filter)
    // useEffect(() => {
    //   setCurrentPage(1);
    //   setFilterPayloadForApi({
    //     status: selectedStatus,
    //     // searchTerm: searchInput, // If server-side search
    //   });
    // }, [selectedStatus]);


    // Pagination handlers
    const itemsPerPageOptions = [10, 20, 30, 50, 100];
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };
    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };
    
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newFile = e.target.files?.[0];
        if (newFile) {
            validateImage(
                newFile, 'picture',
                () => { setUserPicture(newFile); },
                2, ['image/jpeg', 'image/jpg', 'image/png'], { picture: { width: 200, height: 200 } }
            );
        }
    };
    
    const resetValidationErrors = () => {
        setFirstNameValidationError('');
        setLastNameError('');
        setUserPicture(null); // Reset picture state
        if(imageRef.current) imageRef.current.value = ''; // Clear file input
    };

    // Navigation handlers
    const handleViewOwnerUserDetails = (user: OwnerUser) => navigate("/owner-users/view-owner-user", { state: { user } });

    // Add logs handler if applicable
    // const handleOwnerUserLogs = (user: OwnerUser) => navigate("/owner-users/logs", { state: { id: user.id } });


    // Removed client-side sorting. Server-side sorting needed.
    // const handleSort remains, but it should update a state that's part of filterPayloadForApi for server-side sort

    return (
        <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
            {/* Card Header */}
            <div className='card-header border-0 pt-5'>
                <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bold fs-3 mb-1'>Owner Users List</span>
                    <span className='text-muted mt-1 fw-semibold fs-7'>
                        {totalItems > 0 ? `${totalItems} users found` : 'No users'}
                    </span>
                </h3>
                {/* Add Owner User button can be added here if permission allows */}
            </div>

            {/* Search (and optional filter) Section */}
            <div className='card-header border-0 pt-5'>
                 <div className='w-100 d-flex flex-column flex-md-row justify-content-between align-items-md-center'>
                    <div className='mb-3 mb-md-0'>
                        <div className='input-group'>
                            <span className="input-group-text"><KTIcon iconName='magnifier' className='fs-3' /></span>
                            <input
                                type='text'
                                className='form-control form-control-solid w-250px'
                                placeholder='Search name, email...'
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>
                    </div>
                    {/* Add Status Filter Dropdown here if needed for Owner Users */}
                    {/* <div className='d-flex align-items-center'>
                        <span className='text-muted me-2'>Filter by:</span>
                        <select
                            className='form-select form-select-solid form-select-sm w-auto'
                            value={selectedStatus} // Assuming you add selectedStatus state
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            disabled={isFetching}
                        >
                            <option value=''>All Status</option>
                            <option value='1'>Active</option>
                            <option value='0'>Inactive</option>
                        </select>
                    </div> */}
                </div>
            </div>

            {/* Table Section */}
            <div className='card-body py-3'>
                {isFetching && !isLoading && <div className='text-center text-muted p-3'><i>Updating user list...</i></div>}
                <div className='table-responsive'>
                    <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                        <thead>
                            <tr className='fw-bold text-muted bg-light'>
                                <th className='ps-4 min-w-50px rounded-start'>Picture</th>
                                <th className='min-w-190px'>Full Name</th>
                                <th className='min-w-200px'>Email</th>
                                <th className='min-w-120px'>Created At</th>
                                <th className='min-w-150px'>Updated By</th>
                                <th className='min-w-120px'>Updated At</th>
                                {(user_delete || user_update || user_view || user_activity_log) &&
                                    <th className='min-w-150px text-center rounded-end'>Actions</th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                             {isLoading && currentPage === 1 ? (
                                <tr><td colSpan={7} className="text-center py-5"><div className='spinner-border text-primary' /></td></tr>
                            ) : isError ? (
                                <tr><td colSpan={7} className="text-center py-5 text-danger">Error: {error?.message}</td></tr>
                            ) : displayedUsersList.length > 0 ? (
                                displayedUsersList.map((admin) => ( // Using 'admin' as item variable from your map
                                    <tr key={admin.id}>
                                        <td className='ps-4'>
                                            <div className='symbol symbol-circle symbol-40px'>
                                                {admin.profile_picture ? (
                                                    <img src={admin.profile_picture as string} alt={admin.first_name || 'User'} />
                                                ) : (
                                                    <div className="symbol-label fs-2 fw-bold text-inverse-primary bg-light-primary">
                                                        {(admin.first_name || 'U')[0].toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className='text-dark fw-bold text-hover-primary d-block fs-6 cursor-pointer' onClick={() => handleViewOwnerUserDetails(admin)}>
                                                {admin.full_name || `${admin.first_name || ''} ${admin.middle_name || ''} ${admin.last_name || ''}`.trim()}
                                            </span>
                                        </td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{admin.email}</span></td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithTime(admin.created_at)}</span></td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{admin.update_by_admin_name || 'N/A'}</span></td>
                                        <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithTime(admin.updated_at)}</span></td>
                                        {(user_delete || user_update || user_view || user_activity_log) &&
                                        <td className='text-center'>
                                            <div className='d-flex justify-content-center flex-shrink-0'>
                                                {user_view && (<button onClick={() => handleViewOwnerUserDetails(admin)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="View"><KTIcon iconName='eye' className='fs-3' /></button>)}
                                                {user_update && (<button onClick={() => { setSelectedAdmin(admin); resetValidationErrors(); }} data-bs-toggle='modal' data-bs-target='#kt_modal_edit_owner_user' className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Edit"><KTIcon iconName='pencil' className='fs-3' /></button>)}
                                                {/* {user_activity_log && (<button onClick={() => handleOwnerUserLogs(admin)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Logs"><KTIcon iconName='notepad-bookmark' className='fs-3' /></button>)} */}
                                                {user_delete && (<button onClick={() => setSelectedAdmin(admin)} className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' title="Delete" data-bs-toggle='modal' data-bs-target='#kt_modal_delete_owner_user'><KTIcon iconName='trash' className='fs-3' /></button>)}
                                            </div>
                                        </td>
                                        }
                                    </tr>
                                ))
                            ) : (
                                !isFetching && <tr><td colSpan={7} className="text-center py-5">No owner users found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer: Pagination and Items Per Page */}
            {totalItems > 0 && (
                <div className='card-footer d-flex justify-content-between align-items-center py-4'>
                    <div>
                        <span className='text-muted me-2'>Showing:</span>
                        <select
                            className='form-select form-select-sm d-inline-block w-auto me-3'
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            disabled={isFetching}
                        >
                            {itemsPerPageOptions.map(option => (<option key={option} value={option}>{option}</option>))}
                        </select>
                        <span className='text-muted'>
                            {currentRangeFrom} - {currentRangeTo > totalItems ? totalItems : currentRangeTo} of {totalItems} users
                        </span>
                    </div>
                
                    <nav>
                        <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 || isFetching ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={handlePreviousPage} disabled={currentPage === 1 || isFetching}>Previous</button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages || totalPages === 0 || isFetching ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0 || isFetching}>Next</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}

            {/* Delete Confirmation Modal (ID: kt_modal_delete_owner_user) */}
            <div className='modal fade' tabIndex={-1} id='kt_modal_delete_owner_user'>
                {/* ... Modal Content (same as before, ensure IDs are unique if multiple modals on one page) ... */}
                <div className='modal-dialog'>
                    <div className='modal-content'>
                        <div className='modal-header'><h5 className='modal-title'>Delete Owner User</h5><button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button></div>
                        <div className='modal-body'>
                            <p>Are you sure you want to delete: <strong>{selectedAdmin?.full_name || selectedAdmin?.first_name}</strong>?</p>
                        </div>
                        <div className='modal-footer'>
                            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                            <button type="button" className="btn btn-danger" onClick={() => { if (selectedAdmin) handleDeleteAdmin.mutate(selectedAdmin.id);}} data-bs-dismiss="modal" disabled={handleDeleteAdmin.isLoading}>
                                {handleDeleteAdmin.isLoading ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Owner User Modal (ID: kt_modal_edit_owner_user) */}
            <div className='modal fade' tabIndex={-1} id='kt_modal_edit_owner_user'>
                 {/* ... Edit Modal Content (from your provided code, ensure IDs unique) ... */}
                 <div className='modal-dialog modal-dialog-centered modal-lg'>
                    <div className='modal-content'>
                        <div className='modal-header'><h5 className='modal-title'>Edit Owner User</h5><button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={resetValidationErrors}></button></div>
                        <div className='modal-body'>
                            {selectedAdmin && (
                                <>
                                 <div className='mb-2 row mb-6'><label className='col-lg-4 col-form-label required fw-bold fs-6'>Email</label><div className='col-lg-8 fv-row'><label className='form-control form-control-lg form-control-solid user-input'>{selectedAdmin.email || ""}</label></div></div>
                                 <div className='mb-2 row mb-6'><label className='col-lg-4 col-form-label required fw-bold fs-6'>First Name</label><div className='col-lg-8 fv-row'><input value={selectedAdmin.first_name || ""} placeholder='First Name' className='form-control form-control-lg form-control-solid' onChange={(e) => { if (!e.target.value) {setFirstNameValidationError('First name is required.')} else {setFirstNameValidationError('')} setSelectedAdmin({...selectedAdmin, first_name: e.target.value, full_name: `${e.target.value} ${selectedAdmin.middle_name || ''} ${selectedAdmin.last_name || ''}`.trim()})}} type='text' />{firstNameValidationError && (<div className='text-danger mt-1'>{firstNameValidationError}</div>)}</div></div>
                                 <div className='row mb-6'><label className='col-lg-4 col-form-label fw-bold fs-6'>Middle Name</label><div className='col-lg-8 fv-row'><input type='text' className='form-control form-control-lg form-control-solid user-input' placeholder='Middle Name' value={selectedAdmin.middle_name || ""} onChange={(e) => setSelectedAdmin({...selectedAdmin, middle_name: e.target.value, full_name: `${selectedAdmin.first_name || ''} ${e.target.value} ${selectedAdmin.last_name || ''}`.trim()})}/></div></div>
                                 <div className='mb-2 row mb-6'><label className='col-lg-4 required col-form-label fw-bold fs-6'>Last Name</label><div className='col-lg-8 fv-row'><input value={selectedAdmin.last_name || ""} placeholder='Last Name' className='form-control form-control-lg form-control-solid' onChange={(e) => { if (!e.target.value) {setLastNameError('Last Name is Required.')} else {setLastNameError('')} setSelectedAdmin({...selectedAdmin, last_name: e.target.value, full_name: `${selectedAdmin.first_name || ''} ${selectedAdmin.middle_name || ''} ${e.target.value}`.trim()})}} type='text' />{lastNameError && (<div className='text-danger mt-1'>{lastNameError}</div>)}</div></div>
                                 {/* <div className='row mb-6'><label className='col-lg-4 col-form-label fw-bold fs-6'>Mobile Number</label><div className='col-lg-8 fv-row'><input type='text' pattern="[0-9]*" className='form-control form-control-lg form-control-solid user-input' placeholder='Mobile Number' value={selectedAdmin.mobile_no || ""} onChange={(e) => { const val = e.target.value; if (/^\d*$/.test(val)) setSelectedAdmin({...selectedAdmin, mobile_no: val })}}/></div></div> */}
                                 <div className='row mb-6'><label className='col-lg-4 col-form-label fw-bold fs-6'>Picture</label><div className='col-lg-8 fv-row'><input type='file' ref={imageRef} className='form-control form-control-lg form-control-solid' onChange={handleImageChange} name='picture' accept="image/png, image/jpeg, image/jpg"/><div className='text-muted mt-2 fs-7'>Recommended: 200x200px, Max: 2MB, Types: jpg, jpeg, png.</div>{userPicture ? (<div className='mt-2 border p-2 d-inline-block'><img src={URL.createObjectURL(userPicture)} alt='New' width={100} height={100}/></div>) : selectedAdmin?.profile_picture && (<div className='mt-2 border p-2 d-inline-block'><img src={selectedAdmin.profile_picture as string} alt='Current' width={100} height={100}/></div>)}</div></div>
                                 <div className="row mb-6"><label className="col-lg-4 col-form-label fw-bold fs-6"><span>Status</span></label><div className="col-lg-8 fv-row"><Form.Select className='form-control form-control-lg form-control-solid' value={selectedAdmin.status.toString()} onChange={(e) => setSelectedAdmin({...selectedAdmin, status: Number(e.target.value)}) }><option value="1">Active</option><option value="0">Inactive</option></Form.Select></div></div>
                                </>
                            )}
                        </div>
                        <div className='modal-footer'>
                            <button type='button' className='btn btn-light' data-bs-dismiss='modal' onClick={resetValidationErrors}>Close</button>
                            <button type='button' className='btn btn-primary' disabled={!!firstNameValidationError || !!lastNameError || handleUpdateAdmin.isLoading} onClick={() => { if (selectedAdmin && selectedAdmin.first_name && selectedAdmin.last_name) { handleUpdateAdmin.mutate({ id: selectedAdmin.id, first_name: selectedAdmin.first_name, middle_name: selectedAdmin.middle_name || '', last_name: selectedAdmin.last_name, mobile_no: selectedAdmin.mobile_no || '', status: selectedAdmin.status, userPicture: userPicture, }); } }} data-bs-dismiss='modal' >
                                {handleUpdateAdmin.isLoading ? 'Updating...' : 'Update User'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};
export default UsersList